"use client";

/**
 * CollabV3FeeCard 컴포넌트
 * @description 상세의 `Entry fee` 카드(앱 `CollabDetailFeeCard`) — 가격(회차 카드면 `/ session`),
 *   남은 자리 또는 회차 요약 · 회차 줄, 결제 안내, 회색 취소 조건, 본인 확인. 조건부이고 회차가
 *   없으면 최소 인원 진행(`CollabV3ConditionalFee`)을 그린다.
 *
 *   **보기 전용이다.** 예약 · 결제 · 취소는 앱에서 하고, 그 길은 하단 앱 버튼 하나다.
 *   회차 날짜를 표시 시간대로 찍어야 해서 클라이언트 컴포넌트다.
 * @example <CollabV3FeeCard fee={detail.fee} isHostVerified />
 */
import { Card } from "@fanwork-web/design-system";
import { memo, useMemo, type ReactNode } from "react";

import CollabV3Badge from "./CollabV3Badge";
import { collabV3CardStyles as styles } from "./CollabV3Card.styles";
import type { CollabV3FeeCardProps } from "./CollabV3Card.types";
import CollabV3ConditionalFee from "./CollabV3ConditionalFee";
import CollabV3FeeSessions from "./CollabV3FeeSessions";
import CollabV3VerifiedRow from "./CollabV3VerifiedRow";

import { COLLAB_V3_CARD_COPY } from "../../constants/collabV3CardCopy";
import type { CollabV3Fee } from "../../entities";
import { feeBadgeOf } from "../../utils/collabV3FeeBadge";
import { formatCount } from "../../utils/collabV3Format";

const copy = COLLAB_V3_CARD_COPY.fee;

function captionOf(fee: CollabV3Fee, amount: string): string {
  if (fee.isFree) return copy.caption.free;
  return fee.isConditional
    ? copy.caption.conditional(amount)
    : copy.caption.confirmed(amount);
}

/** 회차도 조건부도 아니면 남은 자리 한 줄 — 최대 인원이 없으면 없다 */
function seatsOf(fee: CollabV3Fee): string | null {
  if (fee.remainingSeats === null) return null;
  return fee.remainingSeats > 0
    ? copy.seatsLeft(fee.remainingSeats)
    : copy.seatFull;
}

/** 가격 아래 진행 줄 — 조건부 진행 · 회차 · 남은 자리 중 하나 */
function renderProgress(fee: CollabV3Fee): ReactNode {
  if (fee.conditionalState) return <CollabV3ConditionalFee fee={fee} />;
  if (fee.sessions.length > 0) {
    return <CollabV3FeeSessions sessions={fee.sessions} />;
  }
  const seats = seatsOf(fee);
  return seats ? <p className={styles.meta}>{seats}</p> : null;
}

const CollabV3FeeCard = memo<CollabV3FeeCardProps>(({ fee, isHostVerified }) => {
  const amount = useMemo(() => formatCount(fee.amount), [fee.amount]);
  const badge = useMemo(() => feeBadgeOf(fee), [fee]);
  const policy =
    fee.policy ??
    (fee.isConditional ? copy.policy.conditional : copy.policy.confirmed);

  return (
    <Card
      slot={
        <div className={styles.content}>
          <div className={styles.head}>
            <h3 className={styles.title}>{copy.title}</h3>
            {badge ? <CollabV3Badge {...badge} /> : null}
          </div>
          <p className={styles.amountRow}>
            <span className={styles.amount}>
              {fee.isFree ? copy.free : COLLAB_V3_CARD_COPY.ls(amount)}
            </span>
            {fee.sessions.length > 0 ? (
              <span className={styles.unit}>{copy.unitSession}</span>
            ) : null}
          </p>
          {renderProgress(fee)}
          <p className={styles.body}>{captionOf(fee, amount)}</p>
          <p className={styles.info}>{policy}</p>
          {isHostVerified ? <CollabV3VerifiedRow /> : null}
        </div>
      }
    />
  );
});

CollabV3FeeCard.displayName = "CollabV3FeeCard";

export default CollabV3FeeCard;
